import { useState, useEffect, useRef, useCallback } from "react";
import "../CSS/ConsignmentBooking.css";
import useDraggableScroll from "../hooks/useDraggableScroll";


// ── External: JsBarcode is loaded via CDN script tag in index.html:
// <script src="https://cdnjs.cloudflare.com/ajax/libs/JsBarcode/3.11.5/JsBarcode.all.min.js"></script>

/* ════════════════════════════════════════════════
   DATA
════════════════════════════════════════════════ */
const PARTIES = {
  TML: { name: "Tata Motors Ltd.", gst: "27AAACT2727Q1ZW", rate: 18, basis: "perkg" },
  MSI: { name: "Maruti Suzuki India", gst: "08AAACM4685R1Z5", rate: 22, basis: "perkg" },
  BAL: { name: "Bajaj Auto Ltd.", gst: "27AAACB0534F1Z5", rate: 15, basis: "perkg" },
  BSH: { name: "Bosch India", gst: "29AAACB4849M1ZX", rate: 25, basis: "perpkg" },
  MNL: { name: "Motherson Neeraj Ltd.", gst: "07AABCM1787R1ZA", rate: 20, basis: "perkg" },
  SVP: { name: "Sundaram Valeo Pvt.", gst: "33AAACS1049Q1ZX", rate: 17, basis: "perkg" },
};
const PARTY_JOBS = {
  TML: ["Truck Division", "Car Division", "EV Parts", "After-Market", "Exports", "R&D Spares"],
  MSI: ["Passenger Cars", "Commercial", "Export Parts", "Dealer Supply", "OEM Parts"],
  BAL: ["2W Division", "3W Division", "EV Division", "International", "Racing"],
  BSH: ["Auto Parts", "Power Tools", "Security", "Healthcare", "Thermotechnology"],
  MNL: ["Mirrors", "Wiring Harness", "Polymer", "Vision Systems", "Wheels"],
  SVP: ["Transmission", "Clutch", "Wiper Systems", "Lighting", "Thermal"],
};
const PINCODES = {
  MUM: "400001", DEL: "110001", CHN: "600001", BLR: "560001", HYD: "500001",
  AHM: "380001", PUN: "411001", KOL: "700001", JAI: "302001",
};
const PLACE_NAMES = {
  MUM: "Mumbai Central Hub", DEL: "Delhi NCR Depot", CHN: "Chennai Warehouse",
  BLR: "Bengaluru Facility", HYD: "Hyderabad Hub", AHM: "Ahmedabad Branch",
  PUN: "Pune Branch", KOL: "Kolkata Centre", JAI: "Jaipur Depot",
};
const CNORS = {
  A: { name: "Acme Spares Pvt. Ltd.", contact: "Rakesh Joshi", mobile: "9820012345", addr: "Plot 45, MIDC, Andheri East, Mumbai", gst: "27AACCA1234A1Z1", pin: "400093" },
  B: { name: "BharatForge Components", contact: "Suresh Patil", mobile: "9890056789", addr: "Mundhwa, Pune", gst: "27AABCB4567B2Z2", pin: "411036" },
  C: { name: "Castrol India Ltd.", contact: "Anita Sharma", mobile: "9821123456", addr: "Maker Chambers, Nariman Point, Mumbai", gst: "27AAACC8910C3Z3", pin: "400021" },
  D: { name: "Denso India Pvt. Ltd.", contact: "Priya Reddy", mobile: "9971234567", addr: "IMT Manesar, Gurugram", gst: "06AAACD2345D4Z4", pin: "122050" },
  E: { name: "Excel Engineering", contact: "Kiran Kumar", mobile: "9941234567", addr: "Ambattur Industrial Estate, Chennai", gst: "33AAACE5678E5Z5", pin: "600058" },
};
const CNEES = {
  A: { name: "Ashok Leyland Service", contact: "Mani T.", mobile: "9942345678", addr: "Hosur Road, Hosur, Tamil Nadu", gst: "33AAACA9012A1Z9", pin: "635109" },
  B: { name: "BharatBenz Workshop", contact: "Ramesh K.", mobile: "9810012345", addr: "NH8, Shahdara, New Delhi", gst: "07AAACB3456B2Z8", pin: "110032" },
  C: { name: "Classic Auto Parts", contact: "Deepa N.", mobile: "9886012345", addr: "Bommasandra, Bengaluru", gst: "29AAACC7890C3Z7", pin: "560099" },
  D: { name: "Drive Tech Auto", contact: "Sunil M.", mobile: "9848012345", addr: "Balanagar, Hyderabad", gst: "36AAACD2345D4Z6", pin: "500037" },
  E: { name: "Eicher Motors Workshop", contact: "Govind S.", mobile: "9414012345", addr: "Amer Road, Jaipur", gst: "08AAACE6789E5Z5", pin: "302028" },
};
const PKG_TYPES = ["Box / Carton", "Wooden Crate", "Pallet", "Drum", "Bag / Sack", "Bundle", "Loose / Naked"];

/* ════════════════════════════════════════════════
   HELPERS
════════════════════════════════════════════════ */
function fmt(n) {
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
function calcExpDeliveryDate(priority) {
  const days = { standard: 3, express: 1, urgent: 0 };
  const d = new Date(2026, 3, 14);
  d.setDate(d.getDate() + (days[priority] || 3));
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${String(d.getDate()).padStart(2, "0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
}
function generateCNRValue() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(2);
  const n = String(Math.floor(Math.random() * 99999) + 1).padStart(5, "0");
  return `STL-${yy}${mm}-${n}`;
}
function getRowTotals(piece) {
  const { qty, l, w, h, actW } = piece;
  const volPc = (l * w * h) / 5000;
  const volTot = volPc * qty;
  const actTot = actW * qty;
  return { volTot, actTot, chgTot: Math.max(actTot, volTot) };
}

/* ════════════════════════════════════════════════
   SEARCHABLE DROPDOWN COMPONENT
════════════════════════════════════════════════ */
function SearchableSelect({ value, onChange, options, placeholder, className, style, readOnly }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = options.find(o => o.value === value)?.label || "";

  useEffect(() => {
    if (!open) setQuery(selectedLabel);
  }, [open, selectedLabel]);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick, true);
    return () => document.removeEventListener("mousedown", handleClick, true);
  }, []);

  if (readOnly) {
    return (
      <input
        type="text"
        value={selectedLabel}
        readOnly
        className={className}
        style={style}
        placeholder={placeholder}
      />
    );
  }

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes((query || "").toLowerCase())
  );

  function hl(text, q) {
    if (!q || !q.trim()) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark>{text.slice(i, i + q.length)}</mark>
        {text.slice(i + q.length)}
      </>
    );
  }

  function choose(opt) {
    onChange(opt.value);
    setOpen(false);
  }

  function handleKeyDown(e) {
    const items = filtered;
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "Enter" && items.length === 1) { choose(items[0]); }
  }

  return (
    <div className={`sd-wrap${open ? " open" : ""}`} ref={wrapRef} style={style}>
      <div className="sd-box">
        <input
          ref={inputRef}
          type="text"
          className={`sd-input${className ? " " + className : ""}`}
          placeholder={placeholder}
          value={open ? query : selectedLabel}
          autoComplete="off"
          spellCheck={false}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={handleKeyDown}
        />
        {value && (
          <span className="sd-clear vis" onMouseDown={e => { e.preventDefault(); onChange(""); setQuery(""); inputRef.current?.focus(); }}>
            &times;
          </span>
        )}
        <span
          className="sd-chevron"
          onMouseDown={e => { e.preventDefault(); setOpen(o => !o); inputRef.current?.focus(); }}
        >&#9662;</span>
      </div>
      {open && (
        <div className="sd-drop open">
          {filtered.length === 0
            ? <div className="sd-empty">No match for "{query}"</div>
            : filtered.map(opt => (
              <div
                key={opt.value}
                className={`sd-opt${opt.value === value ? " sel" : ""}`}
                onMouseDown={e => { e.preventDefault(); choose(opt); }}
              >{hl(opt.label, query)}</div>
            ))
          }
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════ */
export default function ConsignmentBooking() {

  /* ── CNR state ── */
  const [currentCNR, setCurrentCNR] = useState(() => generateCNRValue());
  const [cnrEditing, setCnrEditing] = useState(false);
  const [cnrInput, setCnrInput] = useState("");
  const [cnrModeTag, setCnrModeTag] = useState("Auto-generated");
  const [cnrModeColor, setCnrModeColor] = useState("rgba(255,255,255,.55)");

  /* ── Step ── */
  const [currentStep, setCurrentStep] = useState(1);

  /* ── Route ── */
  const [bookingPlace, setBookingPlace] = useState("");
  const [bookingPin, setBookingPin] = useState("");
  const [bpinInfo, setBpinInfo] = useState("");
  const [deliveryPlace, setDeliveryPlace] = useState("");
  const [deliveryPin, setDeliveryPin] = useState("");
  const [dpinInfo, setDpinInfo] = useState("");
  const [priority, setPriority] = useState("standard");
  const [expDelivery, setExpDelivery] = useState("");

  /* ── Party ── */
  const [partySel, setPartySel] = useState("");
  const [partyGst, setPartyGst] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [freightTerms, setFreightTerms] = useState("prepaid");

  /* ── Consignor ── */
  const [cnorMode, setCnorMode] = useState("list");
  const [cnorSel, setCnorSel] = useState("");
  const [cnorName, setCnorName] = useState("");
  const [cnorContact, setCnorContact] = useState("");
  const [cnorMobile, setCnorMobile] = useState("");
  const [cnorAddr, setCnorAddr] = useState("");
  const [cnorGst, setCnorGst] = useState("");
  const [cnorPin, setCnorPin] = useState("");

  /* ── Consignee ── */
  const [cneeMode, setCneeMode] = useState("list");
  const [cneeSel, setCneeSel] = useState("");
  const [cneeName, setCneeName] = useState("");
  const [cneeContact, setCneeContact] = useState("");
  const [cneeMobile, setCneeMobile] = useState("");
  const [cneeAddr, setCneeAddr] = useState("");
  const [cneeGst, setCneeGst] = useState("");
  const [cneePin, setCneePin] = useState("");

  /* ── OTP ── */
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpMobile, setOtpMobile] = useState("");
  const [otpChannel, setOtpChannel] = useState("both");
  const [otpValidity, setOtpValidity] = useState("30");
  const [otpAttempts, setOtpAttempts] = useState("3");

  /* ── Notifications ── */
  const [notifMaster, setNotifMaster] = useState(false);
  const [notifSms, setNotifSms] = useState(true);
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifWa, setNotifWa] = useState(false);
  const [smsMobile, setSmsMobile] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [waNum, setWaNum] = useState("");

  /* ── Pieces ── */
  const [pieces, setPieces] = useState([]);
  const [pieceCounter, setPieceCounter] = useState(0);
  const [commodity, setCommodity] = useState("");
  const [declVal, setDeclVal] = useState("");
  const [specialInst, setSpecialInst] = useState("");

  /* ── Rate ── */
  const [rateBasis, setRateBasis] = useState("perkg");
  const [baseRate, setBaseRate] = useState("");
  const [fuelPct, setFuelPct] = useState(12);
  const [gstPct, setGstPct] = useState("5");
  const [insAmt, setInsAmt] = useState("");
  const [otherAmt, setOtherAmt] = useState("");

  /* ── Images ── */
  const [uploadedImages, setUploadedImages] = useState([]);
  const [curImgType, setCurImgType] = useState("Package");

  /* ── Modals ── */
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showStickerModal, setShowStickerModal] = useState(false);
  const [confirmData, setConfirmData] = useState({});
  const [stickerData, setStickerData] = useState({});

  const stepperRef = useDraggableScroll();
  const pieceTableRef = useDraggableScroll();

  /* ── Toast ── */
  const [toast, setToast] = useState({ show: false, msg: "", type: "" });
  const toastTimer = useRef(null);

  const barcodeSvgRef = useRef(null);

  /* ── INIT ── */
  useEffect(() => {
    setExpDelivery(calcExpDeliveryDate("standard"));
    const d = new Date();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    // Add initial pieces
    addPieceInit("Engine Parts", "Box / Carton", 2, 60, 40, 35, 12.5);
    addPieceInit("Brake Pads", "Box / Carton", 1, 30, 20, 15, 3.0);
    // eslint-disable-next-line
  }, []);

  /* ── TOAST ── */
  function showToast(msg, type = "") {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ show: true, msg, type });
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
  }

  /* ── CNR ── */
  function handleGenerateCNR() {
    const v = generateCNRValue();
    setCurrentCNR(v);
    setCnrModeTag("Auto-generated");
    setCnrModeColor("rgba(255,255,255,.55)");
    setCnrEditing(false);
    showToast("CNR generated", "ok");
  }
  function handleEnableCNREdit() {
    setCnrInput(currentCNR);
    setCnrEditing(true);
    setCnrModeTag("Manual entry…");
    setCnrModeColor("rgba(249,168,37,.85)");
  }
  function handleSaveCNR() {
    const v = cnrInput.trim().toUpperCase();
    if (!v) { showToast("Enter a consignment number", "err"); return; }
    setCurrentCNR(v);
    setCnrModeTag("✎ Manually entered");
    setCnrModeColor("rgba(249,168,37,.9)");
    setCnrEditing(false);
    showToast("CNR set: " + v, "ok");
  }
  function handleCancelCNR() {
    setCnrEditing(false);
    setCnrModeTag("Auto-generated");
    setCnrModeColor("rgba(255,255,255,.55)");
  }

  /* ── STEPPER ── */
  function goStep(n) {
    setCurrentStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /* ── ROUTE ── */
  function handlePlaceChange(type, val) {
    if (type === "booking") {
      setBookingPlace(val);
      if (val && PINCODES[val]) {
        setBookingPin(PINCODES[val]);
        setBpinInfo("📍 " + PINCODES[val] + " — " + (PLACE_NAMES[val] || val));
      } else {
        setBpinInfo("");
      }
    } else {
      setDeliveryPlace(val);
      if (val && PINCODES[val]) {
        setDeliveryPin(PINCODES[val]);
        setDpinInfo("📍 " + PINCODES[val] + " — " + (PLACE_NAMES[val] || val));
      } else {
        setDpinInfo("");
      }
    }
    setExpDelivery(calcExpDeliveryDate(priority));
  }
  function handlePriorityChange(val) {
    setPriority(val);
    setExpDelivery(calcExpDeliveryDate(val));
  }

  /* ── PARTY ── */
  function handlePartyChange(k) {
    setPartySel(k);
    setSelectedJob(null);
    if (!k) { setPartyGst(""); setBaseRate(""); return; }
    const p = PARTIES[k];
    setPartyGst(p.gst);
    setBaseRate(String(p.rate));
    setRateBasis(p.basis);
  }

  /* ── CONSIGNOR / CONSIGNEE FILL ── */
  function handleCnorSel(k) {
    setCnorSel(k);
    if (!k) return;
    const d = CNORS[k];
    setCnorName(d.name); setCnorContact(d.contact); setCnorMobile(d.mobile);
    setCnorAddr(d.addr); setCnorGst(d.gst); setCnorPin(d.pin);
  }
  function handleCneeSel(k) {
    setCneeSel(k);
    if (!k) return;
    const d = CNEES[k];
    setCneeName(d.name); setCneeContact(d.contact); setCneeMobile(d.mobile);
    setCneeAddr(d.addr); setCneeGst(d.gst); setCneePin(d.pin);
  }

  /* ── OTP ── */
  function handleToggleOTP(checked) {
    setOtpEnabled(checked);
    if (checked && cneeMobile) setOtpMobile(cneeMobile);
  }

  /* ── PIECES ── */
  function makePiece(desc, pkgType, qty, l, w, h, actW) {
    const id = Date.now() + Math.random();
    return { id, desc: desc || "", pkgType: pkgType || "Box / Carton", qty: qty || 1, l: l || "", w: w || "", h: h || "", actW: actW || "" };
  }
  function addPieceInit(desc, pkgType, qty, l, w, h, actW) {
    setPieces(prev => [...prev, makePiece(desc, pkgType, qty, l, w, h, actW)]);
    setPieceCounter(c => c + 1);
  }
  function addPiece() {
    setPieces(prev => [...prev, makePiece("", "Box / Carton", 1, "", "", "", "")]);
    setPieceCounter(c => c + 1);
  }
  function delPiece(id) {
    setPieces(prev => prev.filter(p => p.id !== id));
  }
  function clearPieces() {
    setPieces([]);
    setPieceCounter(0);
  }
  function updatePiece(id, field, value) {
    setPieces(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  }

  /* ── TOTALS ── */
  function getTotals() {
    let tQty = 0, tAct = 0, tVol = 0, tChg = 0;
    pieces.forEach(p => {
      const qty = +p.qty || 0, l = +p.l || 0, w = +p.w || 0, h = +p.h || 0, actW = +p.actW || 0;
      const volTot = (l * w * h / 5000) * qty;
      const actTot = actW * qty;
      tQty += qty; tAct += actTot; tVol += volTot; tChg += Math.max(actTot, volTot);
    });
    return { tQty, tAct, tVol, tChg };
  }

  /* ── RATE CALC ── */
  function calcRate() {
    const { tQty, tChg } = getTotals();
    const br = parseFloat(baseRate) || 0;
    const fp = parseFloat(fuelPct) || 0;
    const gp = parseFloat(gstPct) || 0;
    const ins = parseFloat(insAmt) || 0;
    const other = parseFloat(otherAmt) || 0;
    let freight = 0;
    if (rateBasis === "perkg") freight = br * tChg;
    else if (rateBasis === "perpkg") freight = br * tQty;
    else freight = br;
    const fuel = freight * fp / 100;
    const sub = freight + fuel + ins + other;
    const gst = sub * gp / 100;
    const total = sub + gst;
    return { freight, fuel, ins, other, sub, gst, total };
  }

  const rates = calcRate();
  const totals = getTotals();

  /* ── IMAGES ── */
  function handleFiles(files) {
    Array.from(files).forEach(f => {
      if (uploadedImages.length >= 20) { showToast("Max 20 files allowed", "err"); return; }
      if (f.size > 5 * 1024 * 1024) { showToast(f.name + " exceeds 5 MB", "err"); return; }
      const id = Date.now() + Math.random();
      const reader = new FileReader();
      reader.onload = e => {
        setUploadedImages(prev => [...prev, { id, name: f.name, type: curImgType, src: e.target.result, fileType: f.type }]);
      };
      reader.readAsDataURL(f);
    });
  }
  function removeImg(id) {
    setUploadedImages(prev => prev.filter(i => String(i.id) !== String(id)));
  }

  /* ── CONFIRM ── */
  function handleShowConfirm() {
    setConfirmData({
      cnr: currentCNR,
      route: (PLACE_NAMES[bookingPlace] || bookingPlace || "—") + " → " + (PLACE_NAMES[deliveryPlace] || deliveryPlace || "—"),
      party: partySel ? PARTIES[partySel]?.name || partySel : "—",
      pieces: totals.tQty + " pcs",
      wt: "Actual: " + totals.tAct.toFixed(2) + " kg | Charged: " + totals.tChg.toFixed(2) + " kg",
      imgs: uploadedImages.length ? uploadedImages.length + " file(s)" : "None",
      otp: otpEnabled ? ("Enabled — " + (otpMobile || "No mobile")) : "Not required",
      notif: (() => {
        if (!notifMaster) return "None";
        const chs = [];
        if (notifSms) chs.push("SMS");
        if (notifEmail) chs.push("Email");
        if (notifWa) chs.push("WhatsApp");
        return chs.length ? chs.join(" + ") : "None";
      })(),
      total: "₹ " + fmt(rates.total),
    });
    setShowConfirmModal(true);
  }
  function handleBookIt() {
    setShowConfirmModal(false);
    setStickerData({
      cnr: currentCNR,
      from: bookingPlace || "ORIGIN",
      to: deliveryPlace || "DEST",
      cnor: cnorName || "—",
      cnee: cneeName || "—",
      pcs: totals.tQty + " pcs",
      wt: totals.tChg.toFixed(2) + " kg",
      amt: "₹ " + fmt(rates.total),
    });
    setShowStickerModal(true);
    showToast("Consignment booked successfully!", "ok");
  }

  useEffect(() => {
    if (showStickerModal && barcodeSvgRef.current && currentCNR) {
      setTimeout(() => {
        try {
          if (window.JsBarcode) {
            window.JsBarcode(barcodeSvgRef.current, currentCNR, {
              format: "CODE128", width: 2, height: 52, displayValue: false, margin: 4,
              lineColor: "#1A2940", background: "transparent",
            });
          }
        } catch (e) { }
      }, 100);
    }
  }, [showStickerModal, currentCNR]);

  /* ── PRINT STICKER ── */
  function printSticker() {
    const c = document.getElementById("sticker-print-inner").innerHTML;
    const w = window.open("", "_blank", "width=480,height=600");
    w.document.write(`<html><head><link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Lora:wght@700&display=swap" rel="stylesheet">
  <style>body{font-family:'Plus Jakarta Sans',sans-serif;margin:0;padding:20px;background:#fff;}
  .sticker-logo-row{display:flex;align-items:center;gap:8px;margin-bottom:12px;padding-bottom:10px;border-bottom:1px solid #ddd;}
  .sl-name{font-family:'Lora',serif;font-size:16px;font-weight:700;}
  .s-cnr{font-size:22px;font-weight:800;letter-spacing:2px;text-align:center;margin:8px 0;}
  .s-route{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;margin:10px 0;justify-content:center;}
  .s-info-grid{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-top:10px;}
  .s-cell{background:#EBF4FF;border-radius:6px;padding:7px 9px;}
  .sc-l{font-size:9px;font-weight:800;text-transform:uppercase;color:#8AA5C0;}
  .sc-v{font-size:12px;font-weight:700;}svg{max-width:100%;}.sl-mark{display:none;}</style>
  </head><body>${c}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  }

  /* ── CLEAR ── */
  function clearForm() {
    setBookingPlace(""); setBookingPin(""); setBpinInfo("");
    setDeliveryPlace(""); setDeliveryPin(""); setDpinInfo("");
    setPriority("standard"); setExpDelivery(calcExpDeliveryDate("standard"));
    setPartySel(""); setPartyGst(""); setSelectedJob(null); setFreightTerms("prepaid");
    setCnorMode("list"); setCnorSel(""); setCnorName(""); setCnorContact(""); setCnorMobile(""); setCnorAddr(""); setCnorGst(""); setCnorPin("");
    setCneeMode("list"); setCneeSel(""); setCneeName(""); setCneeContact(""); setCneeMobile(""); setCneeAddr(""); setCneeGst(""); setCneePin("");
    setOtpEnabled(false); setOtpMobile(""); setOtpChannel("both"); setOtpValidity("30"); setOtpAttempts("3");
    setNotifMaster(false); setNotifSms(true); setNotifEmail(true); setNotifWa(false);
    setSmsMobile(""); setEmailAddr(""); setWaNum("");
    clearPieces();
    setUploadedImages([]);
    setCommodity(""); setDeclVal(""); setSpecialInst("");
    setRateBasis("perkg"); setBaseRate(""); setFuelPct(12); setGstPct("5"); setInsAmt(""); setOtherAmt("");
    showToast("Form cleared", "");
  }

  function newBooking() {
    setShowStickerModal(false);
    handleGenerateCNR();
    clearForm();
    goStep(1);
  }

  /* ════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════ */
  const bookingPlaceOpts = [
    { value: "MUM", label: "Mumbai Central Hub" }, { value: "DEL", label: "Delhi NCR Depot" },
    { value: "CHN", label: "Chennai Warehouse" }, { value: "BLR", label: "Bengaluru Facility" },
    { value: "HYD", label: "Hyderabad Hub" }, { value: "AHM", label: "Ahmedabad Branch" },
  ];
  const deliveryPlaceOpts = [
    ...bookingPlaceOpts,
    { value: "PUN", label: "Pune Branch" }, { value: "KOL", label: "Kolkata Centre" }, { value: "JAI", label: "Jaipur Depot" },
  ];
  const partyOpts = [
    { value: "TML", label: "Tata Motors Ltd." }, { value: "MSI", label: "Maruti Suzuki India" },
    { value: "BAL", label: "Bajaj Auto Ltd." }, { value: "BSH", label: "Bosch India" },
    { value: "MNL", label: "Motherson Neeraj Ltd." }, { value: "SVP", label: "Sundaram Valeo Pvt." },
  ];
  const cnorOpts = [
    { value: "A", label: "Acme Spares Pvt. Ltd. | Mumbai" }, { value: "B", label: "BharatForge Components | Pune" },
    { value: "C", label: "Castrol India Ltd. | Mumbai" }, { value: "D", label: "Denso India Pvt. Ltd. | Manesar" },
    { value: "E", label: "Excel Engineering | Chennai" },
  ];
  const cneeOpts = [
    { value: "A", label: "Ashok Leyland Service | Hosur" }, { value: "B", label: "BharatBenz Workshop | Delhi" },
    { value: "C", label: "Classic Auto Parts | Bengaluru" }, { value: "D", label: "Drive Tech Auto | Hyderabad" },
    { value: "E", label: "Eicher Motors Workshop | Jaipur" },
  ];

  return (
    <>
      {/* HEADER */}
      {/* <header className="hdr">
        <div className="logo">
          <div className="logo-mark">
            <svg viewBox="0 0 24 24"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/></svg>
          </div>
          <div>
            <div className="logo-name">Saami <span>Tradestar</span> Logistics</div>
            <div className="logo-sub">Pvt Ltd</div>
          </div>
        </div>
        <div className="hdr-r">
          <div className="live-dot">Live System</div>
          <div className="user-chip"><div className="user-av">RM</div>Rahul M.</div>
        </div>
      </header> */}

      <div className="booking-main">
        <div className="pg-title">
          <h1>New Consignment Booking</h1>
          <p>Fill details to generate a consignment number and barcode sticker</p>
        </div>

        {/* CNR BANNER */}
        <div className="cnr-banner">
          <div className="cnr-l">
            <div className="cnr-lbl">Consignment Number</div>
            {!cnrEditing ? (
              <div id="cnr-display-row" style={{ display: "flex", alignItems: "center", gap: 0 }}>
                <div className="cnr-num">{currentCNR}</div>
                <button className="cnr-edit-btn" onClick={handleEnableCNREdit}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4z" /></svg>
                  Edit
                </button>
              </div>
            ) : (
              <div className="cnr-input-row show">
                <input
                  className="cnr-input"
                  value={cnrInput}
                  placeholder="Enter CNR…"
                  maxLength={30}
                  onChange={e => setCnrInput(e.target.value.toUpperCase())}
                  onKeyDown={e => { if (e.key === "Enter") handleSaveCNR(); if (e.key === "Escape") handleCancelCNR(); }}
                  autoFocus
                />
                <button className="cnr-btn" style={{ background: "rgba(39,161,140,.6)", borderColor: "rgba(39,161,140,.8)" }} onClick={handleSaveCNR}>✓ Apply</button>
                <button className="cnr-btn" onClick={handleCancelCNR}>✕</button>
              </div>
            )}
            <div className="cnr-mode-tag" style={{ color: cnrModeColor }}>{cnrModeTag}</div>
          </div>
          <div className="cnr-r">
            <div className="cnr-date-box">
              <div className="cnr-date-lbl">Booking Date</div>
              <div className="cnr-date-val">14 Apr 2026</div>
            </div>
            <button
              className="cnr-btn"
              style={cnrEditing ? { opacity: ".4", pointerEvents: "none" } : {}}
              onClick={handleGenerateCNR}
            >↻ Regenerate</button>
          </div>
        </div>

        {/* STEPPER */}
        <div className="stepper" ref={stepperRef}>
          {[1, 2, 3].map((i, idx) => (
            <>
              <div
                key={i}
                className={`step${currentStep === i ? " active" : ""}${currentStep > i ? " done" : ""}`}
                onClick={() => goStep(i)}
              >
                <div className="sn">{currentStep > i ? "✓" : i}</div>
                <div>
                  <div className="si-name">{["Route & Party", "Consignor / Consignee", "Shipment & Rate"][idx]}</div>
                  <div className="si-desc">{["Origin, destination & party", "Sender, receiver & OTP", "Pieces, images & billing"][idx]}</div>
                </div>
              </div>
              {idx < 2 && <div className="sdiv" key={"div" + i}></div>}
            </>
          ))}
        </div>

        {/* ══════ STEP 1 ══════ */}
        {currentStep === 1 && (
          <div>
            {/* ROUTE */}
            <div className="card">
              <div className="card-hdr">
                <div className="c-icon blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg></div>
                <div><div className="card-title">Route Information</div><div className="card-sub">Select origin and destination locations</div></div>
              </div>
              <div className="g3">
                <div className="field">
                  <label className="req">Booking Place</label>
                  <SearchableSelect value={bookingPlace} onChange={v => handlePlaceChange("booking", v)} options={bookingPlaceOpts} placeholder="— Select Branch —" />
                </div>
                <div className="field">
                  <label className="req">Booking Pincode</label>
                  <input type="text" value={bookingPin} placeholder="6-digit PIN" maxLength={6} onChange={e => setBookingPin(e.target.value.replace(/\D/g, ""))} />
                  {bpinInfo && <div className="pin-info show">{bpinInfo}</div>}
                </div>
                <div className="field">
                  <label>Booking Date</label>
                  <input type="text" value="2026-04-14" readOnly style={{ background: "#f5f9ff", cursor: "not-allowed" }} />
                </div>
                <div className="field">
                  <label className="req">Delivery Place</label>
                  <SearchableSelect value={deliveryPlace} onChange={v => handlePlaceChange("delivery", v)} options={deliveryPlaceOpts} placeholder="— Select Destination —" />
                </div>
                <div className="field">
                  <label className="req">Destination Pincode</label>
                  <input type="text" value={deliveryPin} placeholder="6-digit PIN" maxLength={6} onChange={e => setDeliveryPin(e.target.value.replace(/\D/g, ""))} />
                  {dpinInfo && <div className="pin-info show">{dpinInfo}</div>}
                </div>
                <div className="field">
                  <label>Expected Delivery</label>
                  <input type="text" value={expDelivery} readOnly style={{ background: "#f5f9ff", cursor: "not-allowed" }} placeholder="Auto-calculated" />
                </div>
              </div>
            </div>

            {/* PARTY */}
            <div className="card">
              <div className="card-hdr">
                <div className="c-icon teal"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg></div>
                <div><div className="card-title">Party & Department</div><div className="card-sub">Select billing account and associated job (one per booking)</div></div>
              </div>
              <div className="g2">
                <div className="field">
                  <label className="req">Party / Account</label>
                  <SearchableSelect value={partySel} onChange={handlePartyChange} options={partyOpts} placeholder="— Select Party —" />
                </div>
                <div className="field">
                  <label>Party GST</label>
                  <input type="text" value={partyGst} readOnly style={{ background: "#f5f9ff" }} placeholder="Auto-filled" />
                </div>
                <div className="field gc2">
                  <label className="req">Job / Department</label>
                  <div className="jobs-wrap">
                    <div className="jobs-hdr">
                      <span>{partySel ? PARTIES[partySel]?.name : "Select a party to load jobs"}</span>
                      <span style={{ color: selectedJob ? "var(--teal)" : "var(--blue)" }}>
                        {selectedJob ? "✓ " + selectedJob : "None selected"}
                      </span>
                    </div>
                    <div className="jobs-body">
                      {!partySel
                        ? <span className="jobs-empty">Select a party first…</span>
                        : (PARTY_JOBS[partySel] || []).map(j => (
                          <span
                            key={j}
                            className={`job-chip${selectedJob === j ? " sel" : ""}`}
                            onClick={() => setSelectedJob(prev => prev === j ? null : j)}
                          >{j}</span>
                        ))
                      }
                    </div>
                  </div>
                </div>
                <div className="field">
                  <label>Freight Terms</label>
                  <select value={freightTerms} onChange={e => setFreightTerms(e.target.value)}>
                    <option value="prepaid">Prepaid (Sender pays)</option>
                    <option value="topay">To Pay (Receiver pays)</option>
                    <option value="foc">FOC (Free of Charge)</option>
                  </select>
                </div>
                <div className="field">
                  <label>Priority</label>
                  <select value={priority} onChange={e => handlePriorityChange(e.target.value)}>
                    <option value="standard">Standard (3 days)</option>
                    <option value="express">Express (24 hrs)</option>
                    <option value="urgent">Urgent (Same Day)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="actions">
              <button className="btn btn-ghost" onClick={clearForm}>✕ Clear</button>
              <button className="btn btn-primary" onClick={() => goStep(2)}>
                Next — Consignor / Consignee <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <div className="g2">
              {/* CONSIGNOR */}
              <div className="card">
                <div className="card-hdr">
                  <div className="c-icon blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg></div>
                  <div><div className="card-title">Consignor</div><div className="card-sub">Who is sending / dispatching</div></div>
                </div>
                <div className="party-block">
                  <div className="pb-hdr">
                    <span className="pb-hdr-l">Entry Mode</span>
                    <div className="mode-row" style={{ fontSize: "11.5px" }}>
                      <button className={`mode-tab${cnorMode === "list" ? " active" : ""}`} onClick={() => setCnorMode("list")}>From List</button>
                      <button className={`mode-tab${cnorMode === "direct" ? " active" : ""}`} onClick={() => setCnorMode("direct")}>Direct Entry</button>
                    </div>
                  </div>
                  <div className="pb-body">
                    {cnorMode === "list" && (
                      <div className="field">
                        <label>Select Consignor</label>
                        <SearchableSelect value={cnorSel} onChange={handleCnorSel} options={cnorOpts} placeholder="— Search & Select —" />
                      </div>
                    )}
                    <div className="field"><label className="req">Name</label><input value={cnorName} placeholder="Company / person name" onChange={e => setCnorName(e.target.value)} /></div>
                    <div className="g2">
                      <div className="field"><label>Contact</label><input value={cnorContact} placeholder="Contact person" onChange={e => setCnorContact(e.target.value)} /></div>
                      <div className="field"><label>Mobile</label><input value={cnorMobile} placeholder="10-digit" maxLength={10} onChange={e => setCnorMobile(e.target.value)} /></div>
                    </div>
                    <div className="field"><label>Address</label><textarea value={cnorAddr} placeholder="Full address" rows={2} onChange={e => setCnorAddr(e.target.value)}></textarea></div>
                    <div className="g2">
                      <div className="field"><label>GSTIN</label><input value={cnorGst} placeholder="GST Number" maxLength={15} onChange={e => setCnorGst(e.target.value)} /></div>
                      <div className="field"><label>Pincode</label><input value={cnorPin} placeholder="6-digit" maxLength={6} onChange={e => setCnorPin(e.target.value)} /></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CONSIGNEE */}
              <div className="card">
                <div className="card-hdr">
                  <div className="c-icon teal"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg></div>
                  <div><div className="card-title">Consignee</div><div className="card-sub">Who will receive the shipment</div></div>
                </div>
                <div className="party-block">
                  <div className="pb-hdr">
                    <span className="pb-hdr-l">Entry Mode</span>
                    <div className="mode-row" style={{ fontSize: "11.5px" }}>
                      <button className={`mode-tab${cneeMode === "list" ? " active" : ""}`} onClick={() => setCneeMode("list")}>From List</button>
                      <button className={`mode-tab${cneeMode === "direct" ? " active" : ""}`} onClick={() => setCneeMode("direct")}>Direct Entry</button>
                    </div>
                  </div>
                  <div className="pb-body">
                    {cneeMode === "list" && (
                      <div className="field">
                        <label>Select Consignee</label>
                        <SearchableSelect value={cneeSel} onChange={handleCneeSel} options={cneeOpts} placeholder="— Search & Select —" />
                      </div>
                    )}
                    <div className="field"><label className="req">Name</label><input value={cneeName} placeholder="Company / person name" onChange={e => setCneeName(e.target.value)} /></div>
                    <div className="g2">
                      <div className="field"><label>Contact</label><input value={cneeContact} placeholder="Contact person" onChange={e => setCneeContact(e.target.value)} /></div>
                      <div className="field"><label>Mobile</label><input value={cneeMobile} placeholder="10-digit" maxLength={10} onChange={e => setCneeMobile(e.target.value)} /></div>
                    </div>
                    <div className="field"><label>Address</label><textarea value={cneeAddr} placeholder="Full address" rows={2} onChange={e => setCneeAddr(e.target.value)}></textarea></div>
                    <div className="g2">
                      <div className="field"><label>GSTIN</label><input value={cneeGst} placeholder="GST Number" maxLength={15} onChange={e => setCneeGst(e.target.value)} /></div>
                      <div className="field"><label>Pincode</label><input value={cneePin} placeholder="6-digit" maxLength={6} onChange={e => setCneePin(e.target.value)} /></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* OTP */}
            <div className="card">
              <div className="card-hdr">
                <div className="c-icon blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></svg></div>
                <div><div className="card-title">OTP Delivery Confirmation</div><div className="card-sub">Require one-time password from consignee at delivery</div></div>
              </div>
              <div className="otp-card">
                <div className="otp-hdr">
                  <div className="otp-hdr-l">
                    <label className="sw">
                      <input type="checkbox" checked={otpEnabled} onChange={e => handleToggleOTP(e.target.checked)} />
                      <span className="sw-track"></span>
                    </label>
                    <div>
                      <div style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--ink)" }}>Enable OTP for Delivery</div>
                      <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>Consignee must provide OTP before goods are handed over</div>
                    </div>
                  </div>
                  <span className="otp-badge" style={otpEnabled ? { background: "rgba(39,161,140,.15)", color: "var(--teal)" } : {}}>
                    {otpEnabled ? "Active" : "Off"}
                  </span>
                </div>
                {otpEnabled && (
                  <div className="otp-body">
                    <div className="otp-info">🔐 An OTP is generated at delivery time and sent to the consignee mobile. Delivery agent must enter it before handing over.</div>
                    <div className="g2">
                      <div className="field"><label className="req">Consignee OTP Mobile</label><input value={otpMobile} placeholder="10-digit number" maxLength={10} onChange={e => setOtpMobile(e.target.value.replace(/\D/g, ""))} /></div>
                      <div className="field"><label>OTP Via</label>
                        <select value={otpChannel} onChange={e => setOtpChannel(e.target.value)}>
                          <option value="both">SMS + WhatsApp</option>
                          <option value="sms">SMS Only</option>
                          <option value="wa">WhatsApp Only</option>
                        </select>
                      </div>
                      <div className="field"><label>OTP Validity</label>
                        <select value={otpValidity} onChange={e => setOtpValidity(e.target.value)}>
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="60">1 hour</option>
                        </select>
                      </div>
                      <div className="field"><label>Max Attempts</label>
                        <select value={otpAttempts} onChange={e => setOtpAttempts(e.target.value)}>
                          <option value="3">3 attempts</option>
                          <option value="5">5 attempts</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* NOTIFICATIONS */}
            <div className="card">
              <div className="card-hdr">
                <div className="c-icon teal"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" /></svg></div>
                <div style={{ flex: 1 }}><div className="card-title">Booking Notifications</div><div className="card-sub">Optional — notify consignor &amp; consignee at each milestone</div></div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: notifMaster ? "var(--teal)" : "var(--muted)" }}>{notifMaster ? "ON" : "OFF"}</span>
                  <label className="sw"><input type="checkbox" checked={notifMaster} onChange={e => setNotifMaster(e.target.checked)} /><span className="sw-track"></span></label>
                </div>
              </div>
              {notifMaster && (
                <div className="notif-grid">
                  {/* SMS */}
                  <div className={`nc${notifSms ? " on" : ""}`}>
                    <div className="nc-hdr"><div className="nc-ico sms">📱</div><div className="nc-title">SMS</div>
                      <label className="sw" style={{ marginLeft: "auto" }}><input type="checkbox" checked={notifSms} onChange={e => setNotifSms(e.target.checked)} /><span className="sw-track"></span></label>
                    </div>
                    {notifSms && (
                      <div className="nc-body">
                        <div className="field"><label>Mobile</label><input value={smsMobile} placeholder="10-digit number" onChange={e => setSmsMobile(e.target.value)} /></div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> Booking Confirmed</label>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> Out for Delivery</label>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> Delivered</label>
                          <label className="nc-ev"><input type="checkbox" /> Delay / Exception</label>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* EMAIL */}
                  <div className={`nc${notifEmail ? " on" : ""}`}>
                    <div className="nc-hdr"><div className="nc-ico em">✉️</div><div className="nc-title">Email</div>
                      <label className="sw" style={{ marginLeft: "auto" }}><input type="checkbox" checked={notifEmail} onChange={e => setNotifEmail(e.target.checked)} /><span className="sw-track"></span></label>
                    </div>
                    {notifEmail && (
                      <div className="nc-body">
                        <div className="field"><label>Email</label><input value={emailAddr} placeholder="email@company.com" onChange={e => setEmailAddr(e.target.value)} /></div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> Booking + LR Copy</label>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> In Transit</label>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> Delivery Confirmation</label>
                          <label className="nc-ev"><input type="checkbox" /> Invoice Copy</label>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* WHATSAPP */}
                  <div className={`nc${notifWa ? " on" : ""}`}>
                    <div className="nc-hdr"><div className="nc-ico wa">💬</div><div className="nc-title">WhatsApp</div>
                      <label className="sw" style={{ marginLeft: "auto" }}><input type="checkbox" checked={notifWa} onChange={e => setNotifWa(e.target.checked)} /><span className="sw-track"></span></label>
                    </div>
                    {notifWa && (
                      <div className="nc-body">
                        <div className="field"><label>WhatsApp No.</label><input value={waNum} placeholder="91XXXXXXXXXX" onChange={e => setWaNum(e.target.value)} /></div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> Booking (with barcode)</label>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> Out for Delivery + ETA</label>
                          <label className="nc-ev"><input type="checkbox" defaultChecked /> Delivered + POD</label>
                          <label className="nc-ev"><input type="checkbox" /> Delay Alert</label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="actions">
              <button className="btn btn-ghost" onClick={() => goStep(1)}>← Back</button>
              <button className="btn btn-primary" onClick={() => goStep(3)}>
                Next — Shipment Details <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        )}

        {/* ══════ STEP 3 ══════ */}
        {currentStep === 3 && (
          <div>
            {/* PIECE-WISE */}
            <div className="card">
              <div className="card-hdr">
                <div className="c-icon amber"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg></div>
                <div><div className="card-title">Piece-wise Shipment Details</div><div className="card-sub">Each piece entered separately — volumetric weight auto-calculated (L×W×H ÷ 5000)</div></div>
              </div>
              <div className="g3" style={{ marginBottom: 16 }}>
                <div className="field gc2"><label className="req">Commodity / Description</label><input value={commodity} placeholder="e.g. Engine Parts, Brake Pads, Clutch Assemblies" onChange={e => setCommodity(e.target.value)} /></div>
                <div className="field"><label>Declared Value (₹)</label><input type="number" value={declVal} placeholder="0" onChange={e => setDeclVal(e.target.value)} /></div>
                <div className="field gc3"><label>Special Instructions</label><input value={specialInst} placeholder="e.g. Fragile — Handle with care, Keep upright…" onChange={e => setSpecialInst(e.target.value)} /></div>
              </div>
              <div className="piece-wrap" ref={pieceTableRef}>
                <table className="piece-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th style={{ textAlign: "left", minWidth: 130 }}>Description / Marks</th>
                      <th style={{ minWidth: 110 }}>Pkg Type</th>
                      <th style={{ minWidth: 58 }}>Qty</th>
                      <th style={{ minWidth: 68 }}>L (cm)</th>
                      <th style={{ minWidth: 68 }}>W (cm)</th>
                      <th style={{ minWidth: 68 }}>H (cm)</th>
                      <th style={{ minWidth: 80 }}>Act Wt/pc (kg)</th>
                      <th className="vol-cell" style={{ minWidth: 80 }}>Vol Wt (kg)</th>
                      <th className="chg-cell" style={{ minWidth: 88 }}>Charged Wt (kg)</th>
                      <th style={{ width: 36 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pieces.map((p, i) => {
                      const qty = +p.qty || 0, l = +p.l || 0, w = +p.w || 0, h = +p.h || 0, actW = +p.actW || 0;
                      const volTot = (l * w * h / 5000) * qty;
                      const actTot = actW * qty;
                      const chgTot = Math.max(actTot, volTot);
                      return (
                        <tr key={p.id}>
                          <td>{i + 1}</td>
                          <td><input type="text" value={p.desc} placeholder="Marks / Description" onChange={e => updatePiece(p.id, "desc", e.target.value)} style={{ textAlign: "left", minWidth: 120 }} /></td>
                          <td>
                            <select value={p.pkgType} onChange={e => updatePiece(p.id, "pkgType", e.target.value)} style={{ minWidth: 100 }}>
                              {PKG_TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                          </td>
                          <td><input type="number" value={p.qty} min={1} placeholder="1" onChange={e => updatePiece(p.id, "qty", e.target.value)} style={{ minWidth: 50 }} /></td>
                          <td><input type="number" value={p.l} min={0} step=".1" placeholder="0" onChange={e => updatePiece(p.id, "l", e.target.value)} style={{ minWidth: 58 }} /></td>
                          <td><input type="number" value={p.w} min={0} step=".1" placeholder="0" onChange={e => updatePiece(p.id, "w", e.target.value)} style={{ minWidth: 58 }} /></td>
                          <td><input type="number" value={p.h} min={0} step=".1" placeholder="0" onChange={e => updatePiece(p.id, "h", e.target.value)} style={{ minWidth: 58 }} /></td>
                          <td><input type="number" value={p.actW} min={0} step=".01" placeholder="0.00" onChange={e => updatePiece(p.id, "actW", e.target.value)} style={{ minWidth: 68 }} /></td>
                          <td className="vol-cell">{volTot > 0 ? volTot.toFixed(2) + " kg" : "—"}</td>
                          <td className="chg-cell">{chgTot > 0 ? chgTot.toFixed(2) + " kg" : "—"}</td>
                          <td><button className="del-btn" onClick={() => delPiece(p.id)} title="Remove">✕</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="tot-lbl">TOTALS</td>
                      <td className="tot-v">{totals.tQty}</td>
                      <td colSpan={3}></td>
                      <td className="tot-v">{totals.tAct.toFixed(2)} kg</td>
                      <td className="tot-v">{totals.tVol.toFixed(2)} kg</td>
                      <td className="tot-c">{totals.tChg.toFixed(2)} kg</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                <div className="piece-bar">
                  <span className="piece-hint">💡 Volumetric Wt = L×W×H ÷ 5000 × Qty &nbsp;|&nbsp; Charged = higher of Actual vs Volumetric</span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-ghost" style={{ padding: "7px 15px", fontSize: "12.5px" }} onClick={addPiece}>+ Add Piece</button>
                    <button className="btn btn-ghost" style={{ padding: "7px 15px", fontSize: "12.5px", color: "var(--coral)", borderColor: "rgba(224,90,90,.3)" }} onClick={clearPieces}>Clear All</button>
                  </div>
                </div>
              </div>
              <div className="wt-strip">
                <div className="wt-box blue"><div className="wb-lbl">Total Pieces</div><div className="wb-val">{totals.tQty}</div><div className="wb-unit">packages</div></div>
                <div className="wt-box amber"><div className="wb-lbl">Actual Weight</div><div className="wb-val">{totals.tAct.toFixed(2)}</div><div className="wb-unit">kg</div></div>
                <div className="wt-box blue"><div className="wb-lbl">Volumetric Weight</div><div className="wb-val">{totals.tVol.toFixed(2)}</div><div className="wb-unit">kg</div></div>
                <div className="wt-box teal-hl"><div className="wb-lbl">✓ Charged Weight</div><div className="wb-val">{totals.tChg.toFixed(2)}</div><div className="wb-unit">kg — used for billing</div></div>
              </div>
            </div>

            {/* IMAGES */}
            <div className="card">
              <div className="card-hdr">
                <div className="c-icon blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg></div>
                <div><div className="card-title">Shipment Images</div><div className="card-sub">Attach photos of packages, labels, documents or damage evidence</div></div>
              </div>
              <div className="img-type-row">
                {["Package", "Label", "Invoice", "Damage", "Other"].map((t, i) => (
                  <button key={t} className={`img-type-btn${curImgType === t ? " active" : ""}`} onClick={() => setCurImgType(t)}>
                    {["📦", "🏷", "📄", "⚠️", "📎"][i]} {t}
                  </button>
                ))}
              </div>
              <div
                className="upload-zone"
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("drag"); }}
                onDragLeave={e => e.currentTarget.classList.remove("drag")}
                onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("drag"); handleFiles(e.dataTransfer.files); }}
              >
                <input type="file" accept="image/*,.pdf" multiple onChange={e => handleFiles(e.target.files)} />
                <div className="uz-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg></div>
                <div className="uz-title">Drag &amp; drop here, or click to browse</div>
                <div className="uz-sub">Selected type: <strong>{curImgType}</strong> &nbsp;|&nbsp; JPG · PNG · PDF · Max 5 MB each</div>
              </div>
              {uploadedImages.length > 0 && (
                <div className="img-grid">
                  {uploadedImages.map(img => (
                    <div key={img.id} className="img-item">
                      {img.fileType === "application/pdf"
                        ? <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>📄</div>
                        : <img src={img.src} alt={img.name} />
                      }
                      <span className="img-tag">{img.type}</span>
                      <div className="img-lbl">{img.name}</div>
                      <button className="img-del" onClick={() => removeImg(img.id)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              {uploadedImages.length > 0 && (
                <div className="img-count-bar">
                  <span>{uploadedImages.length} file{uploadedImages.length !== 1 ? "s" : ""} attached</span>
                  <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: 12, color: "var(--coral)" }} onClick={() => setUploadedImages([])}>Remove All</button>
                </div>
              )}
            </div>

            {/* RATE CARD */}
            <div className="card">
              <div className="card-hdr">
                <div className="c-icon coral"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg></div>
                <div><div className="card-title">Rate Calculation</div><div className="card-sub">Auto-computed from party rate card — override allowed with approval</div></div>
              </div>
              <div className="g2">
                <div className="g2">
                  <div className="field"><label>Rate Basis</label>
                    <select value={rateBasis} onChange={e => setRateBasis(e.target.value)}>
                      <option value="perkg">Per Kg</option>
                      <option value="perpkg">Per Package</option>
                      <option value="fixed">Fixed Rate</option>
                    </select>
                  </div>
                  <div className="field"><label>Base Rate (₹)</label><input type="number" value={baseRate} readOnly style={{ background: "#f5f9ff" }} placeholder="Auto from party" /></div>
                  <div className="field"><label>Fuel Surcharge %</label><input type="number" value={fuelPct} step="0.5" onChange={e => setFuelPct(e.target.value)} /></div>
                  <div className="field"><label>GST %</label>
                    <select value={gstPct} onChange={e => setGstPct(e.target.value)}>
                      <option value="0">0%</option>
                      <option value="5">5%</option>
                      <option value="12">12%</option>
                      <option value="18">18%</option>
                    </select>
                  </div>
                  <div className="field"><label>Insurance (₹)</label><input type="number" value={insAmt} placeholder="0" onChange={e => setInsAmt(e.target.value)} /></div>
                  <div className="field"><label>Other Charges (₹)</label><input type="number" value={otherAmt} placeholder="0" onChange={e => setOtherAmt(e.target.value)} /></div>
                </div>
                <div>
                  <div className="rate-display">
                    <div className="r-row"><span className="r-lbl">Freight Charge</span><span className="r-val">₹ {fmt(rates.freight)}</span></div>
                    <div className="r-row"><span className="r-lbl">Fuel Surcharge</span><span className="r-val">₹ {fmt(rates.fuel)}</span></div>
                    <div className="r-row"><span className="r-lbl">Insurance</span><span className="r-val">₹ {fmt(rates.ins)}</span></div>
                    <div className="r-row"><span className="r-lbl">Other Charges</span><span className="r-val">₹ {fmt(rates.other)}</span></div>
                    <div className="r-row"><span className="r-lbl">Sub Total</span><span className="r-val">₹ {fmt(rates.sub)}</span></div>
                    <div className="r-row"><span className="r-lbl">GST ({gstPct}%)</span><span className="r-val">₹ {fmt(rates.gst)}</span></div>
                    <div className="r-row r-total"><span className="r-lbl">Total Payable</span><span className="r-val">₹ {fmt(rates.total)}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="actions">
              <button className="btn btn-ghost" onClick={() => goStep(2)}>← Back</button>
              <button className="btn btn-teal" onClick={handleShowConfirm}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Confirm &amp; Book
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CONFIRM MODAL */}
      {showConfirmModal && (
        <div className="overlay show">
          <div className="modal">
            <div className="modal-hd">
              <h3>Confirm Booking</h3>
              <p>Review details before finalising. CNR and barcode will be generated on confirmation.</p>
            </div>
            <div className="modal-body">
              <div className="modal-details">
                {[
                  ["CNR Number", <span style={{ color: "var(--blue)", fontFamily: "'Lora',serif", fontSize: 15, letterSpacing: 1 }}>{confirmData.cnr}</span>],
                  ["Route", confirmData.route],
                  ["Party", confirmData.party],
                  ["Pieces", confirmData.pieces],
                  ["Charged Weight", confirmData.wt],
                  ["Images", confirmData.imgs],
                  ["OTP Delivery", confirmData.otp],
                  ["Notifications", confirmData.notif],
                ].map(([label, val]) => (
                  <div key={label} className="modal-row"><span>{label}</span><span>{val}</span></div>
                ))}
                <div className="modal-row" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                  <span style={{ fontWeight: 800, color: "var(--teal)", fontSize: 14 }}>Total Amount</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: "var(--teal)" }}>{confirmData.total}</span>
                </div>
              </div>
            </div>
            <div className="modal-ft">
              <button className="btn btn-ghost" onClick={() => setShowConfirmModal(false)}>Cancel</button>
              <button className="btn btn-teal" onClick={handleBookIt}>✓ Confirm &amp; Book</button>
            </div>
          </div>
        </div>
      )}

      {/* STICKER OVERLAY */}
      {showStickerModal && (
        <div className="sticker-overlay show">
          <div className="sticker-card">
            <div className="sticker-hd">
              <div><h3>Consignment Sticker Ready</h3><p>Print and affix to all packages before dispatch</p></div>
              <button className="sticker-close" onClick={() => setShowStickerModal(false)}>✕</button>
            </div>
            <div className="sticker-body">
              <div className="sticker-print" id="sticker-print">
                <div id="sticker-print-inner">
                  <div className="sticker-logo-row">
                    <div className="sl-mark"><svg viewBox="0 0 24 24"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" /></svg></div>
                    <div className="sl-name">Saami Tradestar Logistics Pvt Ltd</div>
                  </div>
                  <div className="s-cnr">{stickerData.cnr}</div>
                  <svg ref={barcodeSvgRef} style={{ width: "100%" }}></svg>
                  <div className="s-route">
                    <span>{stickerData.from}</span>
                    <div className="arrow"></div>
                    <span>{stickerData.to}</span>
                  </div>
                  <div className="s-info-grid">
                    <div className="s-cell"><div className="sc-l">Consignor</div><div className="sc-v">{stickerData.cnor}</div></div>
                    <div className="s-cell"><div className="sc-l">Consignee</div><div className="sc-v">{stickerData.cnee}</div></div>
                    <div className="s-cell"><div className="sc-l">Pieces</div><div className="sc-v">{stickerData.pcs}</div></div>
                    <div className="s-cell"><div className="sc-l">Charged Wt</div><div className="sc-v">{stickerData.wt}</div></div>
                    <div className="s-cell"><div className="sc-l">Date</div><div className="sc-v">14 Apr 2026</div></div>
                    <div className="s-cell"><div className="sc-l">Amount</div><div className="sc-v" style={{ color: "var(--teal)" }}>{stickerData.amt}</div></div>
                  </div>
                </div>
              </div>
              <div className="sticker-actions">
                <button className="btn btn-ghost" onClick={() => setShowStickerModal(false)} style={{ flex: 1 }}>Close</button>
                <button className="btn btn-amber" onClick={printSticker} style={{ flex: 1 }}>🖨 Print</button>
                <button className="btn btn-primary" onClick={newBooking} style={{ flex: 1 }}>+ New Booking</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      <div className={`toast${toast.show ? " show" : ""}${toast.type ? " " + toast.type : ""}`}>
        <span>{toast.msg}</span>
      </div>
    </>
  );
}
